import React, { useState, useEffect } from 'react';
import BBALoader from '../../components/BBALoader';
import { useLocation } from 'react-router-dom';
import { Search, Filter, Package, ShoppingCart } from 'lucide-react';
import BackButton from '../../components/BackButton';
import Modal from '../../components/Modal';
import StartSidebar from '../../components/StartSidebar';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import { submitPortalFulfillmentRequest } from '../../services/portalFulfillmentRequestService';
import { normalizeAlaCarteItem } from '../../utils/alaCarteCatalog';
import { sanitizeAlaCarteForPublic } from '../../utils/alaCartePublic';
import {
  clampDeliverableQuantity,
  getDeliverableMaxQuantity,
  isAlaCarteItemHiddenFromPurchase,
} from '../../utils/programPricing';
import { areAlaCarteItemsExclusiveChoice } from '../../utils/alaCarteExclusiveChoice';

const AlaCarte = () => {
  const location = useLocation();
  const isPortalRoute = location.pathname.startsWith('/portal/');
  const { currentUser } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState('All');
  const [selectedItem, setSelectedItem] = useState(null);
  const [showItemModal, setShowItemModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [cart, setCart] = useState([]);
  const [showCartModal, setShowCartModal] = useState(false);
  const [isRequested, setIsRequested] = useState(false);
  const [cartSubmitting, setCartSubmitting] = useState(false);

  // Sample items data - replace with actual data from your backend
  const [items, setItems] = useState([]);

  const departments = ['All', 'General', 'Sales', 'Operations', 'Finance', 'Technology', 'Marketing', 'HR'];

  // Load data from Firestore
  useEffect(() => {
    const loadItems = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, 'alaCarteItems'));
        // Drop unpromoted items before sanitizing (sanitize strips pricingStatus).
        const itemsData = querySnapshot.docs
          .map((docSnap) => normalizeAlaCarteItem(docSnap.id, docSnap.data()))
          .filter((item) => !isAlaCarteItemHiddenFromPurchase(item))
          .map(sanitizeAlaCarteForPublic);
        setItems(itemsData);
      } catch (error) {
        console.error('Error loading items:', error);
      } finally {
        setLoading(false);
      }
    };

    loadItems();
  }, []);

  const filteredItems = items.filter(item => {
    // Don't show child items as cards - they belong to a parent
    if (item.pricingType === 'child') return false;
    // Don't show always-included items - they're automatically included, no selection needed
    if (item.pricingType === 'alwaysIncluded') return false;
    const matchesSearch = item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         (item.description || '').toLowerCase().includes(searchQuery.toLowerCase());
    const matchesDepartment = selectedDepartment === 'All' || item.department === selectedDepartment;
    return matchesSearch && matchesDepartment;
  });

  const openItemModal = (item) => {
    setSelectedItem(item);
    setShowItemModal(true);
  };

  const addToCart = (item) => {
    setCart((prev) => {
      const without = prev.filter((row) => !areAlaCarteItemsExclusiveChoice(row, item));
      const existingItem = without.find((c) => c.id === item.id);
      if (existingItem) {
        const nextQty = clampDeliverableQuantity(item, existingItem.quantity + 1);
        return without.map((c) => (c.id === item.id ? { ...c, quantity: nextQty } : c));
      }
      return [...without, { ...item, quantity: clampDeliverableQuantity(item, 1) }];
    });
  };

  const removeFromCart = (itemId) => {
    setCart(cart.filter(item => item.id !== itemId));
  };

  const updateCartQuantity = (itemId, quantity) => {
    setCart((prev) => {
      const cartRow = prev.find((i) => i.id === itemId);
      if (!cartRow) return prev;
      const q = clampDeliverableQuantity(cartRow, quantity);
      if (q <= 0) return prev.filter((i) => i.id !== itemId);
      const updated = prev.map((row) => (row.id === itemId ? { ...row, quantity: q } : row));
      const row = updated.find((i) => i.id === itemId);
      return updated.filter((line) => line.id === itemId || !areAlaCarteItemsExclusiveChoice(line, row));
    });
  };

  const getCartTotal = () => {
    return cart.reduce((total, item) => {
      if (item.pricingType === 'caseByCase') {
        return total; // No price until BBA team establishes estimate
      }
      if (item.pricingType === 'alwaysIncluded') {
        return total; // Always included, no price
      }
      if (item.pricingType === 'packages' || !item.pricingType) {
        // For packages, use the first package's total cost
        const firstPackage = item.packages?.[0];
        if (firstPackage) {
          const packageTotal = (firstPackage.items || []).reduce((sum, pkgItem) => 
            sum + parseFloat(pkgItem.cost || 0), 0
          );
          return total + (packageTotal * item.quantity);
        }
      } else {
        // For standard pricing
        return total + ((item.standardPricing?.cost || 0) * item.quantity);
      }
      return total;
    }, 0);
  };

  const hasCaseByCaseInCart = () => cart.some(item => item.pricingType === 'caseByCase');

  const handleRequest = async () => {
    if (cart.length === 0) return;
    if (isPortalRoute) {
      if (!currentUser?.uid) {
        alert('Please sign in to submit a request.');
        return;
      }
      setCartSubmitting(true);
      try {
        let accountId = '';
        let requestedBy = currentUser.displayName || currentUser.email || '';
        let company = '';
        try {
          const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
          if (userDoc.exists()) {
            const ud = userDoc.data();
            accountId = ud?.accountId || '';
            const full = `${ud?.firstName || ''} ${ud?.lastName || ''}`.trim();
            if (full) requestedBy = full;
            const c = ud?.company;
            if (c && c !== 'Unknown Company') company = c;
          }
        } catch (_) {}
        const cartSnapshot = cart.map((item) => ({
          id: item.id,
          name: item.name,
          quantity: item.quantity,
          department: item.department,
          pricingType: item.pricingType,
          icon: item.icon,
        }));
        await submitPortalFulfillmentRequest({
          kind: 'alacarte',
          source: 'alacarte',
          details: {
            cart: cartSnapshot,
            cartTotalDisplay: getCartTotal(),
            hasCaseByCase: hasCaseByCaseInCart(),
          },
          userId: currentUser.uid,
          userEmail: currentUser.email || '',
          accountId,
          requestedBy,
          company,
        });
        setIsRequested(true);
      } catch (e) {
        console.error(e);
        alert(e?.message || 'Could not submit request.');
      } finally {
        setCartSubmitting(false);
      }
      return;
    }
    setIsRequested(true);
  };

  const handleWithdraw = () => {
    setIsRequested(false);
    setCart([]);
    setShowCartModal(false);
  };

  const getDepartmentColor = (department) => {
    const colors = {
      'Sales': 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
      'Operations': 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
      'Finance': 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
      'Technology': 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
      'Marketing': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
      'HR': 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
      'General': 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
    };
    return colors[department] || colors['General'];
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <BBALoader size={48} className="mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">Loading items...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {!isPortalRoute && <StartSidebar />}
      <div className="max-w-7xl mx-auto py-12 px-4">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <BackButton to={isPortalRoute ? '/portal/billing' : undefined} />
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white flex-1 text-center">
            Custom Solutions
          </h1>
          {/* Cart Icon */}
          <button
            onClick={() => setShowCartModal(true)}
            className="relative p-3 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
          >
            <ShoppingCart className="h-6 w-6" />
            {cart.length > 0 && (
              <span className="absolute -top-2 -right-2 bg-white text-red-600 text-xs font-bold rounded-full h-6 w-6 flex items-center justify-center">
                {cart.reduce((total, item) => total + item.quantity, 0)}
              </span>
            )}
          </button>
        </div>

        {/* Search and Filter Bar */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 mb-6">
          <div className="flex flex-col md:flex-row gap-4 items-center">
            {/* Search Bar */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
              <input
                type="text"
                placeholder="Search items..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-red-500 focus:border-transparent"
              />
            </div>

            {/* Department Filter */}
            <div className="flex items-center gap-2">
              <Filter className="text-gray-400 h-5 w-5" />
              <select
                value={selectedDepartment}
                onChange={(e) => setSelectedDepartment(e.target.value)}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-red-500 focus:border-transparent"
              >
                {departments.map(dept => (
                  <option key={dept} value={dept}>{dept}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Items Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredItems.map(item => (
            <div
              key={item.id}
              className="bg-white dark:bg-gray-800 rounded-lg shadow-sm hover:shadow-md transition-shadow cursor-pointer border border-gray-200 dark:border-gray-700"
              onClick={() => openItemModal(item)}
            >
              <div className="p-6 text-center">
                <div className="text-4xl mb-4">{item.icon || '📦'}</div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  {item.name}
                </h3>
                <div className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${getDepartmentColor(item.department || 'General')}`}>
                  {item.department || 'General'}
                </div>
                <div className="mt-3 text-sm text-gray-500 dark:text-gray-400">
                  {item.pricingType === 'caseByCase'
                    ? 'Case by Case — BBA team will connect to establish estimate'
                    : (item.pricingType === 'packages' || !item.pricingType) 
                      ? `${item.packages?.length || 0} package${(item.packages?.length || 0) !== 1 ? 's' : ''} • ${(item.packages || []).reduce((total, pkg) => total + (pkg.items?.length || 0), 0)} items`
                      : `$${(item.standardPricing?.cost || 0).toLocaleString()} per ${item.standardPricing?.unit || 'item'}`
                  }
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Empty State */}
        {filteredItems.length === 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-8 text-center mt-6">
            <div className="mb-4 mx-auto w-12 h-12 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
              <Package className="h-6 w-6 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-1">No items found</h3>
            <p className="text-gray-500 dark:text-gray-400">Try adjusting your search or filter.</p>
          </div>
        )}
      </div>

      {/* Item Details Modal */}
      <Modal isOpen={showItemModal} onClose={() => setShowItemModal(false)} zIndex={50}>
        {selectedItem && (
          <div className="p-6">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">{selectedItem.name}</h2>
              <div className="text-6xl mb-4">{selectedItem.icon || '📦'}</div>
              <div className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${getDepartmentColor(selectedItem.department || 'General')}`}>
                {selectedItem.department || 'General'}
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Description</h3>
                <p className="text-gray-900 dark:text-white">{selectedItem.description}</p>
              </div>

              <div>
                <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3">
                  {selectedItem.pricingType === 'caseByCase' ? 'Pricing' : selectedItem.pricingType === 'term' ? 'Term Pricing' : (selectedItem.pricingType === 'packages' || !selectedItem.pricingType) ? 'Packages' : 'Standard Pricing'}
                </h3>
                {selectedItem.pricingType === 'caseByCase' ? (
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                    <p className="text-gray-900 dark:text-white font-medium">Case by Case</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      The BBA team will connect with you to establish an estimate.
                    </p>
                  </div>
                ) : (selectedItem.pricingType === 'packages' || !selectedItem.pricingType) ? (
                  <div className="space-y-3">
                    {(selectedItem.packages || []).map((pkg, packageIndex) => (
                      <div key={packageIndex} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
                        <div className="flex justify-between items-center mb-2">
                          <span className="font-medium text-gray-900 dark:text-white">{pkg.name || `Package ${packageIndex + 1}`}</span>
                          <span className="text-lg font-bold text-gray-900 dark:text-white">
                            ${(pkg.items || []).reduce((sum, item) => sum + parseFloat(item.cost || 0), 0).toLocaleString()}
                          </span>
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {(pkg.items || []).length} item{(pkg.items || []).length !== 1 ? 's' : ''}
                        </div>
                        <div className="mt-3 space-y-3">
                          {(pkg.items || []).map((item, itemIndex) => (
                            <div key={itemIndex} className="bg-gray-100 dark:bg-gray-600 rounded-lg p-3">
                              <div className="flex justify-between items-center mb-2">
                                <span className="font-medium text-gray-900 dark:text-white">{item.name || `Item ${itemIndex + 1}`}</span>
                              </div>
                              <div className="grid grid-cols-3 gap-2">
                                <span className="text-gray-900 dark:text-white">{item.name || 'Unnamed Item'}</span>
                                <span className="text-lg font-bold text-gray-900 dark:text-white">
                                  ${(item.cost || 0).toLocaleString()}
                                </span>
                                <span className="text-sm text-gray-500 dark:text-gray-400">
                                  per {item.unit || 'item'}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                    <div className="text-center">
                      <div className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                        ${(selectedItem.standardPricing?.cost || 0).toLocaleString()}
                      </div>
                      <div className="text-lg text-gray-600 dark:text-gray-400">
                        per {selectedItem.standardPricing?.unit || 'item'}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-center gap-3 mt-6 pt-4 border-t border-gray-200 dark:border-gray-600">
              <button
                onClick={() => {
                  addToCart(selectedItem);
                  setShowItemModal(false);
                }}
                className="px-6 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
              >
                Add to Cart
              </button>
              <button
                onClick={() => setShowItemModal(false)}
                className="px-6 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Cart Modal */}
      <Modal isOpen={showCartModal} onClose={() => setShowCartModal(false)} zIndex={50}>
        <div className="p-6">
          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              {isRequested ? 'Request Submitted' : 'Shopping Cart'}
            </h2>
            {cart.length === 0 && !isRequested && (
              <div className="text-6xl mb-4">🛒</div>
            )}
          </div>

          {cart.length === 0 && !isRequested ? (
            <div className="text-center py-8">
              <p className="text-gray-500 dark:text-gray-400">Your cart is empty</p>
            </div>
          ) : isRequested ? (
            <div className="text-center py-8">
              <div className="text-6xl mb-4">✅</div>
              <p className="text-gray-500 dark:text-gray-400 mb-4">Your request has been submitted successfully!</p>
              <p className="text-sm text-gray-400 dark:text-gray-500 mb-6">We'll get back to you soon with pricing and next steps.</p>
              <button
                onClick={handleWithdraw}
                className="px-6 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors"
              >
                Withdraw
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {cart.map((item) => {
                const maxQ = getDeliverableMaxQuantity(item);
                const atMax = maxQ != null && item.quantity >= maxQ;
                return (
                <div key={item.id} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-3">
                      <div className="text-2xl">{item.icon || '📦'}</div>
                      <div>
                        <h3 className="font-medium text-gray-900 dark:text-white">{item.name}</h3>
                        <div className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${getDepartmentColor(item.department || 'General')}`}>
                          {item.department || 'General'}
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => removeFromCart(item.id)}
                      className="text-red-500 hover:text-red-700 text-sm"
                    >
                      Remove
                    </button>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => updateCartQuantity(item.id, item.quantity - 1)}
                        className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center text-gray-600 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-500"
                      >
                        -
                      </button>
                      <span className="w-8 text-center font-medium text-gray-900 dark:text-white">
                        {item.quantity}
                      </span>
                      <button
                        onClick={() => updateCartQuantity(item.id, item.quantity + 1)}
                        disabled={atMax}
                        className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center text-gray-600 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        +
                      </button>
                    </div>
                    
                    <div className="text-right">
                      <div className="font-bold text-gray-900 dark:text-white">
                        {item.pricingType === 'caseByCase'
                          ? 'TBD'
                          : `$${(() => {
                              if (item.pricingType === 'packages' || !item.pricingType) {
                                const firstPackage = item.packages?.[0];
                                if (firstPackage) {
                                  const packageTotal = (firstPackage.items || []).reduce((sum, pkgItem) => 
                                    sum + parseFloat(pkgItem.cost || 0), 0
                                  );
                                  return (packageTotal * item.quantity).toLocaleString();
                                }
                              } else {
                                return ((item.standardPricing?.cost || 0) * item.quantity).toLocaleString();
                              }
                              return '0';
                            })()}`
                        }
                      </div>
                    </div>
                  </div>
                </div>
                );
              })}
              
              <div className="border-t border-gray-200 dark:border-gray-600 pt-4">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-lg font-semibold text-gray-900 dark:text-white">Total:</span>
                  <span className="text-2xl font-bold text-gray-900 dark:text-white">
                    ${getCartTotal().toLocaleString()}
                  </span>
                </div>
                {hasCaseByCaseInCart() && (
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                    Some items are priced case by case. The BBA team will connect with you to establish estimates.
                  </p>
                )}
                
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowCartModal(false)}
                    className="flex-1 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors"
                  >
                    Continue Shopping
                  </button>
                  <button
                    type="button"
                    disabled={cartSubmitting || cart.length === 0}
                    onClick={() => void handleRequest()}
                    className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {cartSubmitting ? 'Submitting…' : isPortalRoute ? 'Request items' : 'Request'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default AlaCarte;
